import {
  SCENARIO_APPROVED_STATUS,
  SCENARIO_APPROVAL_STATUS,
} from "../../global/constants";

/**
 * Format the major/minor version of a Scenario or PortfolioScenario for display
 * (e.g. {majorVersion: 2, minorVersion: 1} -> "v2.1"). Falls back to "v1.0"
 * when fields are missing — anything created before the V1_57_8 migration was
 * applied keeps the default 1.0 backfill.
 */
export const formatScenarioVersion = (scenario) => {
  if (!scenario) return "v1.0";
  const major = scenario.majorVersion != null ? scenario.majorVersion : 1;
  const minor = scenario.minorVersion != null ? scenario.minorVersion : 0;
  return `v${major}.${minor}`;
};

export const isApproved = (scenario) =>
  scenario?.status === SCENARIO_APPROVED_STATUS ||
  scenario?.status === SCENARIO_APPROVAL_STATUS ||
  scenario?.approved === true;

const toVersionNumber = (value, fallback) => {
  if (value === null || value === undefined || value === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const getScenarioVersion = (scenario) => ({
  major: toVersionNumber(scenario?.majorVersion, 1),
  minor: toVersionNumber(scenario?.minorVersion, 0),
});

export const compareScenarioVersion = (a, b) => {
  const va = getScenarioVersion(a);
  const vb = getScenarioVersion(b);
  if (va.major !== vb.major) return va.major - vb.major;
  if (va.minor !== vb.minor) return va.minor - vb.minor;
  return 0;
};

export const getScenarioTimestamp = (scenario) => {
  if (scenario?.updatedAt) return new Date(scenario.updatedAt).getTime() || 0;
  if (scenario?.createdAt) return new Date(scenario.createdAt).getTime() || 0;
  return 0;
};

const toId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Pick the scenario the Schedule View should land on when the URL carries no
 * usable scenario id. By definition a project that has scenarios always has one
 * selected, so this must return a scenario whenever the list holds one.
 *
 * Preference order:
 *   1. the project's own currentScenarioId, when that scenario is in the list;
 *   2. highest version — major, then minor (missing versions read as 1.0);
 *   3. approved over non-approved;
 *   4. most recently updated (falling back to created);
 *   5. highest id, so the pick never depends on the order the API returned.
 */
export const pickDefaultScenario = (scenarios) => {
  if (!Array.isArray(scenarios) || !scenarios.length) return null;

  const candidates = scenarios.filter(Boolean);
  if (!candidates.length) return null;

  const current = candidates.find(
    (scenario) =>
      scenario.project?.currentScenarioId != null &&
      String(scenario.id) === String(scenario.project.currentScenarioId)
  );
  if (current) return current;

  return candidates.reduce((best, scenario) => {
    const versionDiff = compareScenarioVersion(scenario, best);
    if (versionDiff !== 0) return versionDiff > 0 ? scenario : best;

    const approvedDiff = Number(isApproved(scenario)) - Number(isApproved(best));
    if (approvedDiff !== 0) return approvedDiff > 0 ? scenario : best;

    const timestampDiff =
      getScenarioTimestamp(scenario) - getScenarioTimestamp(best);
    if (timestampDiff !== 0) return timestampDiff > 0 ? scenario : best;

    return toId(scenario.id) > toId(best.id) ? scenario : best;
  });
};
